import type { OnlineBookingTab } from "@/pages/stammdaten/onlineBookingSections";

export type OnlineBookingRegelnSub = "saisons" | "mindestaufenthalt" | "cutoff";
export type OnlineBookingStreckeSub = "schritte" | "gastdaten";
export type OnlineBookingAngebotSub = "anreisearten" | "extras" | "darstellung";

export type OnlineBookingSubSection =
  | OnlineBookingRegelnSub
  | OnlineBookingStreckeSub
  | OnlineBookingAngebotSub;

export interface OnlineBookingSubSectionDef {
  id: OnlineBookingSubSection;
  label: string;
  shortLabel: string;
  description: string;
}

export const ONLINE_BOOKING_REGELN_SUBSECTIONS: OnlineBookingSubSectionDef[] = [
  {
    id: "saisons",
    label: "Buchungssaisons",
    shortLabel: "Saisons",
    description: "In welchen Zeiträumen Gäste online buchen dürfen",
  },
  {
    id: "mindestaufenthalt",
    label: "Mindestaufenthalt",
    shortLabel: "Min. Nächte",
    description: "Mindestanzahl Nächte pro Buchung und Saison",
  },
  {
    id: "cutoff",
    label: "Same-Day-Cutoff",
    shortLabel: "Cutoff",
    description: "Ab welcher Uhrzeit am Anreisetag keine Same-Day-Buchung mehr möglich ist",
  },
];

export const ONLINE_BOOKING_STRECKE_SUBSECTIONS: OnlineBookingSubSectionDef[] = [
  {
    id: "schritte",
    label: "Buchungsschritte",
    shortLabel: "Schritte",
    description: "Welche Optionen Gäste während der Buchung sehen",
  },
  {
    id: "gastdaten",
    label: "Gastdaten & Felder",
    shortLabel: "Gastdaten",
    description: "Pflicht-, Optional- und Ausblende-Regeln für Formularfelder",
  },
];

export const ONLINE_BOOKING_ANGEBOT_SUBSECTIONS: OnlineBookingSubSectionDef[] = [
  {
    id: "anreisearten",
    label: "Anreisearten",
    shortLabel: "Anreise",
    description: "Welche Anreisearten Gäste auf der Buchungswebsite wählen können",
  },
  {
    id: "extras",
    label: "Zusatzartikel",
    shortLabel: "Extras",
    description: "Optionale Extras, die Gäste während der Buchung dazubuchen können",
  },
  {
    id: "darstellung",
    label: "Kategorie-Darstellung",
    shortLabel: "Darstellung",
    description: "Upselling, Preisvergleich und Marketing-Texte in der Kategorieauswahl",
  },
];

export function subSectionsForTab(tab: OnlineBookingTab): OnlineBookingSubSectionDef[] | null {
  switch (tab) {
    case "regeln":
      return ONLINE_BOOKING_REGELN_SUBSECTIONS;
    case "strecke":
      return ONLINE_BOOKING_STRECKE_SUBSECTIONS;
    case "angebot":
      return ONLINE_BOOKING_ANGEBOT_SUBSECTIONS;
    default:
      return null;
  }
}

export function defaultSubSectionForTab(tab: OnlineBookingTab): OnlineBookingSubSection | null {
  const sections = subSectionsForTab(tab);
  return sections?.[0]?.id ?? null;
}

export function parseOnlineBookingSubSection(
  tab: OnlineBookingTab,
  value: string | null,
): OnlineBookingSubSection | null {
  const sections = subSectionsForTab(tab);
  if (!sections) return null;
  const match = sections.find((s) => s.id === value);
  return match?.id ?? sections[0]!.id;
}

export function onlineBookingSubSectionHref(
  tab: OnlineBookingTab,
  subSection: OnlineBookingSubSection,
): string {
  const base = tab === "overview" ? "/stammdaten/online-buchung" : `/stammdaten/online-buchung?tab=${tab}`;
  const defaultSub = defaultSubSectionForTab(tab);
  if (!defaultSub || subSection === defaultSub) {
    return base;
  }
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}section=${subSection}`;
}
